import type { DealerCustomer } from "@/gql/graphql";
import { isCdhIdIamIdMigrationEnabled } from "@/store/dxpIamAuthHeader";
import { useCustomerAccountIdsStore } from "@/store/useCustomerAccountIdsStore";

export type CustomerRouteIds = Pick<DealerCustomer, "id"> & {
  iamId?: DealerCustomer["iamId"] | null;
};

/**
 * Customer id segment used in in-app URLs (`/customers/:customerId/...`).
 * Falls back to the CDH id when the IAM id is unknown; the customer route
 * rewrites that to the IAM id once it resolves the pair.
 */
export function getCustomerRouteId(
  customer: CustomerRouteIds,
  iamIdByCdhId: ReadonlyMap<string, string>
): string {
  if (!isCdhIdIamIdMigrationEnabled()) {
    return customer.id;
  }
  const iamId = customer.iamId ?? iamIdByCdhId.get(customer.id);
  return iamId !== undefined && iamId.length > 0 ? iamId : customer.id;
}

/** For non-React helpers that only know the customer's CDH id. */
export function getStoredCustomerRouteId(cdhId: string): string {
  return getCustomerRouteId(
    { id: cdhId },
    useCustomerAccountIdsStore.getState().iamIdByCdhId
  );
}

const CUSTOMERS_SEGMENT = "customers";
const CREATE_CUSTOMER_SEGMENT = "create";

/** Rewrites a bookmarked/email `/customers/:cdhId` segment to IAM when known. */
export function canonicalizeCustomerPath(
  path: string,
  iamIdByCdhId?: ReadonlyMap<string, string>
): string {
  const suffixIndex = path.search(/[?#]/);
  const pathname = suffixIndex === -1 ? path : path.slice(0, suffixIndex);
  const suffix = suffixIndex === -1 ? "" : path.slice(suffixIndex);

  if (!isCdhIdIamIdMigrationEnabled()) {
    return path;
  }

  const mapping =
    iamIdByCdhId ?? useCustomerAccountIdsStore.getState().iamIdByCdhId;

  const segments = pathname.split("/");
  const customersIndex = segments.findIndex(
    (segment) => segment === CUSTOMERS_SEGMENT
  );
  const routeSegment =
    customersIndex === -1 ? undefined : segments[customersIndex + 1];
  if (
    routeSegment === undefined ||
    routeSegment.length === 0 ||
    routeSegment === CREATE_CUSTOMER_SEGMENT
  ) {
    return path;
  }

  const iamId = getCustomerRouteId({ id: routeSegment }, mapping);
  if (iamId === routeSegment) {
    return path;
  }

  segments[customersIndex + 1] = iamId;
  return segments.join("/") + suffix;
}
